<template>
  <div>
    <b-container class="wide">
      <head-block :title="$t('title.customer.payment_report')" />
      <payment-report-filter
        @filter-applied="updateReportWithFilter"/>
      <content-table
        head-variant="dark"
        :fields="fields"
        :items="paymentReports"
        :slots="templates"
      >
        <template
          slot="created"
          slot-scope="data"
        >{{
          formatDate(data.tbl.item.created, userMarket?.supportCountry?.code, userMarket?.timezone)
        }}</template>
        <template
          slot="period"
          slot-scope="data"
        >{{
          formatDate(data.tbl.item.startDate, userMarket?.supportCountry?.code, userMarket?.timezone)
        }} - {{
          formatDate(data.tbl.item.endDate, userMarket?.supportCountry?.code, userMarket?.timezone)
        }}</template>
        <template slot="actions" slot-scope="data">
          <div class="d-flex justify-content-center">
            <b-button
              size="sm"
              class="mr-1"
              v-b-tooltip.hover
              :title="$t('table.customer_payment_report.pdf_download_btn')"
              variant="primary"
              @click="pdfDownload(data.tbl.item)">
              <b-icon icon="file-earmark-text"/>
            </b-button>
          </div>
        </template>
      </content-table>
      <span v-if="!loader.isActive && !paymentReports.length" class="no-table-data">
        {{ $t('table.general.no_data') }}
      </span>
      <pagination
        :page="page"
        :pages-count="pagesCount"
        :page-size="pageSize"
        :count="count"
        @page-change="clickPaginationCallback"
        @page-size-change="onPageSizeChange" />
    </b-container>
  </div>
</template>

<script>
  import {mapGetters, mapState} from 'vuex'
  import ContentTable from '@base/components/ContentTable.vue'
  import Pagination from '@base/components/Pagination.vue'
  import HeadBlock from '@base/components/HeadBlock.vue'
  import ObjectAction from '@base/mixins/ObjectAction.vue'
  import { saveFile } from '@base/utils'
  import ObjectUtils from "@base/mixins/ObjectUtils.vue";
  import PaymentReportFilter from "@user/components/PaymentReportFilter.vue";
  import Formatting from "@base/mixins/Formatting.vue";

  export default {
    name: 'PaymentReportView',

    components: {
      PaymentReportFilter,
      HeadBlock,
      ContentTable,
      Pagination,
    },

    mixins: [ ObjectAction, ObjectUtils, Formatting ],

    data () {
      return {
        fields: [
          { key: 'created',
            label: this.$t('table.customer_payment_report.created'),
            class: 'text-center'
          },
          { key: 'period',
            label: this.$t('table.customer_payment_report.period'),
            class: 'text-center'
          },
          { key: 'actions',
            label: this.$t('form.general.actions'),
            class: 'text-center'
          },
        ],
        templates: [
          { name: 'created', field: 'created' },
          { name: 'period', field: 'period' },
          { name: 'actions', field: 'actions' },
        ],
        selectedObject: null,
        loader: { isActive: true },
        loading: false,
      }
    },

    computed: {
      ...mapState(['paymentReports', 'userMarket']),
      ...mapGetters({
        page: 'getPaymentReportPage',
        pageSize: 'getPaymentReportPageSize',
        pagesCount: 'getPaymentReportPagesCount',
        count: 'getPaymentReportCount'
      }),
    },

    methods: {
      getPaymentReports () {
        if (this.loading)
          return
        this.loading = true
        this.loader = this.$loading.show(this.$_loaderOptions)
        this.$store.commit(this.$_mutationTypes.SET_PAYMENT_REPORTS_PAGE, 1)
        this.$store.dispatch(this.$_actionTypes.GET_PAYMENT_REPORTS)
          .then((res) => {
            this.$store.commit(this.$_mutationTypes.SET_PAYMENT_REPORTS_PAGES_COUNT, res.totalPages)
            this.$store.commit(this.$_mutationTypes.SET_PAYMENT_REPORTS_COUNT, res.count)
          })
          .catch((error) => {
            this.$_notifyError(error, this)
          })
          .finally(() => {
            this.loader.hide()
            this.loading = false
          })
      },

      clickPaginationCallback (pageNum) {
        if (this.loading)
          return
        this.loading = true
        this.loader = this.$loading.show(this.$_loaderOptions)

        this.$store.commit(this.$_mutationTypes.SET_PAYMENT_REPORTS_PAGE, pageNum)
        this.$store.dispatch(this.$_actionTypes.GET_PAYMENT_REPORTS)
          .then((res) => {
            this.$store.commit(this.$_mutationTypes.SET_PAYMENT_REPORTS_PAGES_COUNT, res.totalPages)
            this.$store.commit(this.$_mutationTypes.SET_PAYMENT_REPORTS_COUNT, res.count)
          })
          .catch((error) => {
            this.$_notifyError(error, this)
          })
          .finally(() => {
            this.loader.hide()
            this.loading = false
          })
      },

      onPageSizeChange (size) {
        if (this.loading)
          return
        this.loading = true
        this.loader = this.$loading.show(this.$_loaderOptions)

        this.$store.dispatch(this.$_actionTypes.CHANGE_TABLE_PAGE_SIZE, size)
        this.$store.dispatch(this.$_actionTypes.GET_PAYMENT_REPORTS)
          .then((res) => {
            this.$store.commit(this.$_mutationTypes.SET_PAYMENT_REPORTS_PAGES_COUNT, res.totalPages)
            this.$store.commit(this.$_mutationTypes.SET_PAYMENT_REPORTS_COUNT, res.count)
          })
          .catch((error) => {
            this.$_notifyError(error, this)
          })
          .finally(() => {
            this.loader.hide()
            this.loading = false
          })
      },

      pdfDownload (item) {
        if (this.loading)
          return
        this.loading = true
        this.loader = this.$loading.show(this.$_loaderOptions)
        this.$store.dispatch(this.$_actionTypes.DOWNLOAD_PAYMENT_REPORT_PDF, item.id)
          .then((response) => {
            saveFile(response.data["file"], response.data["filename"], 'application/pdf')
            this.$notify({
              group: 'app',
              type: 'success',
              title: this.$t('notifications.title.success'),
              text: this.$t(
                'notifications.text.customer_payment_report.success_pdf_downloaded'
              ),
            })
          })
          .catch((error) => {
            this.$_notifyError(error, this)
          })
          .finally(() => {
            this.loader.hide()
            this.loading = false
          })
      },

      updateReportWithFilter () {
        this.$store.commit(this.$_mutationTypes.SET_PAYMENT_REPORTS_PAGE, 1)
        this.getPaymentReports()
      },
    }
  }
</script>

<style>

</style>
